import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { AutoCompleteEnum } from '@inter-ikea-kompis/enums';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ILocalizedInformation, ITheme } from '@inter-ikea-kompis/types';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { InputLayoutEnum } from '@inter-ikea-kompis/component-input';
import { Input } from '@inter-ikea-kompis/component-input';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { checkmarkCircle, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';

import SendByEmailStateEnum from '../../enums/SendByEmailStateEnum.js';
import type ISendByEmailDexfSettings from '../../types/ISendByEmailDexfSettings.js';
import type ISendByEmailTranslations from '../../types/ISendByEmailTranslations.js';
import SendByEmailStyle from './SendByEmailStyle.js';

/* eslint-disable no-control-regex */
const EMAIL_REGEXP =
	/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
/* eslint-enable no-control-regex */

/**
 * @event input Emitted when user write a character in the input field
 * @event validation Show error message or not depending on valid input
 * @event send Click on send button
 * @event reset Sent after 3 seconds after state success.
 */

export default class SendByEmail extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ISendByEmailTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: ISendByEmailDexfSettings | null = null;

	// Valid states can be found in SendByEmailState
	@property({ type: String })
	public state: SendByEmailStateEnum = SendByEmailStateEnum.default;

	// Valid input in input field
	@property({ type: Boolean })
	public invalidInput = false;

	// Enable to allow autocompletion in the browser. Note that it should be disabled in Kiosks due to privacy policies, as it can otherwise store user data.
	@property({ type: Boolean })
	public autocomplete = false;
	// Disables links. This attribute should be set to "true" when in a Kiosk.

	@property({ type: Boolean })
	public disableLinks = false;

	// Email
	@property({ type: String, synchronous: true })
	public value: string | null = null;

	private selectionStart = 0;
	private selectionEnd = 0;
	private timeout: ReturnType<typeof setTimeout> | null = null;

	private dexfSettings: ISendByEmailDexfSettings | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations and settings to be required, but since the props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${SendByEmailStyle.getStyle({ theme: this.theme })}
			</style>
			<div class="container">${this.getInput()} ${this.getButton()}</div>
			${this.getMessage()}
		`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(warningTriangle);
		registerIcon(checkmarkCircle);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-input', Input);

		super.connectedCallback();
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		if (changedProperties.has('state')) {
			if (this.state === SendByEmailStateEnum.success) {
				if (this.timeout) {
					clearTimeout(this.timeout);
				}
				this.timeout = setTimeout(
					() => this.dispatchEvent(new CustomEvent('reset', { bubbles: true })),
					3000
				);
			}
		}
		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): ISendByEmailTranslations {
		return this.translations
			? this.translations
			: <ISendByEmailTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): ISendByEmailDexfSettings {
		return <ISendByEmailDexfSettings>this.dexfSettings;
	}
	/**
	 * Get HTML for input.
	 *
	 * @returns Template.
	 */
	private getInput(): RenderResult {
		const translations = this.getTranslations();
		return html`
			<kompis-input
				@focusOut="${this.onFocusOut.bind(this)}"
				@input="${{ passive: false, handleEvent: this.onInput.bind(this) }}}"
				@keypress="${this.onKeyPress.bind(this)}"
				.theme="${this.theme}"
				.inputMode="${'email'}"
				.inputName="${'email'}"
				.helpMessage="${this.invalidInput ? translations.emailAddressInvalid : '\u200B'}"
				.layout="${this.invalidInput ? InputLayoutEnum.error : InputLayoutEnum.default}"
				.label="${translations.emailAddress}"
				.value="${this.value}"
				.selectionStart="${Math.max(1, this.selectionStart)}"
				.selectionEnd="${Math.max(1, this.selectionEnd)}"
				.autocomplete="${this.autocomplete ? AutoCompleteEnum.email : AutoCompleteEnum.off}"
			></kompis-input>
		`;
	}

	/**
	 * Message to the user.
	 *
	 * @returns Template.
	 */
	private getMessage(): RenderResult {
		return html`${this.getInlineMessage()} ${this.getDisclaimer()}`;
	}

	private getInlineMessage(): RenderResult {
		return html`<div aria-live="polite">${this.getSuccessMessage()}</div>
			<div aria-live="assertive">${this.getFailedMessage()}</div>`;
	}

	private getSuccessMessage(): RenderResult {
		if (this.state !== SendByEmailStateEnum.success) {
			return null;
		}

		const positiveVariant: InlineMessageVariant = 'positive';

		return html`<skapa-inline-message subtle variant="${positiveVariant}">
			<skapa-icon slot="message-icon" icon="checkmark-circle"></skapa-icon>
			<span slot="body">${this.getTranslations().emailSuccessMessage}</span>
		</skapa-inline-message>`;
	}

	private getFailedMessage(): RenderResult {
		if (this.state !== SendByEmailStateEnum.failed) {
			return null;
		}

		const cautionaryVariant: InlineMessageVariant = 'cautionary';

		return html`<skapa-inline-message subtle variant="${cautionaryVariant}">
			<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
			<span slot="body">${this.getTranslations().ikeaGenericErrorTryAgain}</span>
		</skapa-inline-message>`;
	}

	/**
	 * Returns the disclaimer.
	 *
	 * @returns Template.
	 */
	private getDisclaimer(): RenderResult {
		const translations = this.getTranslations();
		const urls = this.getSettings().kompis.urls;

		if (!translations.privacyPolicy || !translations.privacyPolicyNoLinks) {
			return null;
		}

		if (this.disableLinks) {
			return html`
				<div class="message">
					<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
						${translations.privacyPolicyNoLinks}
						<br />
						${urls.privacyPolicyLink.replace('http://', '').replace('https://', '')}
					</kompis-text>
				</div>
			`;
		}

		const links = this.getSplitPrivacyPolicyString(translations.privacyPolicy);

		return html`
			<div class="message">
				${links.map((text, index) => {
					if (index === links.length - 1) {
						return html`
							<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}"
								>${text}</kompis-text
							>
						`;
					}
					return html`
						<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}"
							>${text}</kompis-text
						>
						${this.getPrivacyPolicyLink()}
					`;
				})}
			</div>
		`;
	}

	private getSplitPrivacyPolicyString(text: string): string[] {
		if (text.includes('{{')) {
			return text.split('{{privacy_policy_link}}');
		} else {
			return text.split('[[privacy_policy_link]]');
		}
	}

	/**
	 * Returns the privacy policy link.
	 *
	 * @returns Template.
	 */
	private getPrivacyPolicyLink(): RenderResult {
		return html`
			<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
				<kompis-hyperlink
					.theme="${this.theme}"
					.href="${this.getSettings().kompis.urls.privacyPolicyLink}"
					.target="${'_blank'}"
					.underline="${true}"
					.text="${this.getTranslations().privacyPolicyLinkText}"
				></kompis-hyperlink>
			</kompis-text>
		`;
	}

	/**
	 * Get HTML for button.
	 *
	 * @returns Template.
	 */
	private getButton(): RenderResult {
		const primaryVariant: ButtonVariant = 'primary';

		return html`
			<skapa-button
				variant="${primaryVariant}"
				?loading="${this.state === SendByEmailStateEnum.loading}"
				loadingSrText="${this.getTranslations().loading}"
				size="small"
				@click="${this.onSendClick.bind(this)}"
			>
				${this.getTranslations().buttonSend}
			</skapa-button>
		`;
	}

	/**
	 * Event for focusOut.
	 *
	 * @param event Input event.
	 */
	private onFocusOut(event: CustomEvent): void {
		const { value } = event.detail;
		if (!this.validateInput(value)) {
			if (value.length < 1) {
				this.dispatchValidationEvent({ invalidInput: false });
			} else {
				this.dispatchValidationEvent({ invalidInput: true });
			}
		}
	}

	/**
	 * On enter key pressed event.
	 *
	 * @param event Event.
	 */
	private onKeyPress(event: KeyboardEvent): void {
		if (event.key === 'Enter' && this.validateInput(this.value) && !this.invalidInput) {
			this.onSendClick();
		} else if (event.key === 'Enter' && !this.validateInput(this.value) && !this.invalidInput) {
			this.dispatchValidationEvent({ invalidInput: true });
		}
	}

	/**
	 * Event for onInput.
	 *
	 * @param event Event.
	 */
	private onInput(event: CustomEvent): void {
		this.selectionStart = event.detail.selectionStart;
		this.selectionEnd = event.detail.selectionEnd;

		this.dispatchEvent(
			new CustomEvent('input', { bubbles: true, detail: { value: event.detail.value } })
		);

		if (this.invalidInput && this.validateInput(event.detail.value)) {
			this.dispatchValidationEvent({ invalidInput: false });
		}
	}

	/**
	 * Click event for button and sends email.
	 */
	private onSendClick(): void {
		if (this.invalidInput || !this.validateInput(this.value)) {
			this.dispatchValidationEvent({ invalidInput: true, includeInputElement: true });
		} else {
			this.dispatchEvent(
				new CustomEvent('send', {
					bubbles: true,
					detail: {
						value: this.value
					}
				})
			);
		}
	}

	/**
	 * @param object ...
	 * @param object.invalidInput Whether the input is invalid or not.
	 * @param object.includeInputElement Whether the input element should be included in the event detail.
	 */
	private dispatchValidationEvent({
		invalidInput,
		includeInputElement = false
	}: {
		invalidInput: boolean;
		includeInputElement?: boolean;
	}): void {
		const inputElement = includeInputElement
			? this.shadowRoot
					.querySelector(this.customElements.getInternalName('kompis-input'))
					?.shadowRoot?.querySelector('input')
			: undefined;

		this.dispatchEvent(
			new CustomEvent('validation', {
				bubbles: true,
				detail: {
					invalidInput,
					inputElement
				}
			})
		);
	}

	/**
	 * Returns wether the value matches input mode pattern.
	 *
	 * @param value Input value.
	 * @returns Is valid value.
	 */
	private validateInput(value: string | null): boolean {
		if (!this.value) {
			return false;
		}
		return EMAIL_REGEXP.test(value || '');
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-icon': SkapaIcon;
		'kompis-send-by-email': SendByEmail;
	}
}
